// Copyright 2026, University of Maryland, All Rights Reserved
import type { FC } from 'react'

import { Input, Label } from '@/components/ui'
import { Textarea } from '@/components/ui'
import { MarkdownEditor } from '@/components/ui/markdown-editor/markdown-editor'

import type { KbSchema, KbSchemaField } from '../types'

interface SchemaFieldsEditorProps {
  schema: KbSchema
  values: Record<string, string>
  disabled?: boolean
  onChange: (key: string, value: string) => void
}

/**
 * Render an edit form derived from a KB-item schema. One control per field;
 * `markdown` fields use the MarkdownEditor, `textarea` uses a textarea,
 * `text` uses a single-line input. The description (shown to the user as
 * helper text) is the same string injected into the LLM prompt.
 */
export const SchemaFieldsEditor: FC<SchemaFieldsEditorProps> = ({
  schema,
  values,
  disabled,
  onChange,
}) => (
  <div className="space-y-3">
    {schema.fields.map(field => (
      <SchemaFieldControl
        key={field.key}
        field={field}
        value={values[field.key] ?? ''}
        disabled={disabled}
        onChange={value => onChange(field.key, value)}
      />
    ))}
  </div>
)

interface SchemaFieldControlProps {
  field: KbSchemaField
  value: string
  disabled?: boolean
  onChange: (value: string) => void
}

const SchemaFieldControl: FC<SchemaFieldControlProps> = ({ field, value, disabled, onChange }) => {
  const id = `kb-field-${field.key}`
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {field.label}
        {field.required && <span className="ml-1 text-destructive">*</span>}
      </Label>
      {field.type === 'markdown' ? (
        <MarkdownEditor value={value} onChange={onChange} />
      ) : field.type === 'textarea' ? (
        <Textarea
          id={id}
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          rows={4}
        />
      ) : (
        <Input
          id={id}
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
        />
      )}
      {field.description && (
        <p className="text-[11px] text-muted-foreground">{field.description}</p>
      )}
    </div>
  )
}
